import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import {
  Save, Plus, X, Zap, MessageCircleWarning, ShieldAlert, HelpCircle, Ban, Send,
  Inbox, RefreshCw, Sparkles, ShieldCheck, Clock, AlertTriangle, CheckCircle2,
  History, Settings2, Link2, MailCheck, Loader2, ArrowRight, Search, Filter,
  Unlock, PauseCircle, PlayCircle, TestTube2, Bot, User2, TerminalSquare,
} from "lucide-react";

const NEXON = {
  indigo: "#636AC3",
  indigoInk: "#1E1E3F",
  mint: "#C9E9A4",
  cloud: "#FAF9FE",
  ash: "#7A7A99",
};

const TABS = [
  { key: "inbox",       label: "Inbox",           icon: Inbox },
  { key: "history",     label: "Sent history",    icon: History },
  { key: "auto",        label: "Auto-replies",    icon: Sparkles },
  { key: "connections", label: "Connections",     icon: Link2 },
  { key: "settings",    label: "Settings",        icon: Settings2 },
];

const CATEGORY_META = {
  submission_status: { label: "Submission status", tone: "sky",     icon: Zap },
  how_to_submit:     { label: "How to submit",     tone: "emerald", icon: HelpCircle },
  payment_issue:     { label: "Payment issue",     tone: "amber",   icon: MessageCircleWarning },
  dispute_fraud:     { label: "Dispute / fraud",   tone: "rose",    icon: ShieldAlert },
  general_question:  { label: "General question",  tone: "indigo",  icon: HelpCircle },
  spam_irrelevant:   { label: "Spam / irrelevant", tone: "slate",   icon: Ban },
};

const TONE_CLASSES = {
  sky:     { chip: "bg-sky-100 text-sky-800",         head: "bg-sky-50 border-sky-100 text-sky-800" },
  emerald: { chip: "bg-emerald-100 text-emerald-800", head: "bg-emerald-50 border-emerald-100 text-emerald-800" },
  amber:   { chip: "bg-amber-100 text-amber-800",     head: "bg-amber-50 border-amber-100 text-amber-800" },
  rose:    { chip: "bg-rose-100 text-rose-800",       head: "bg-rose-50 border-rose-100 text-rose-800" },
  indigo:  { chip: "bg-indigo-100 text-indigo-800",   head: "bg-indigo-50 border-indigo-100 text-indigo-800" },
  slate:   { chip: "bg-slate-100 text-slate-800",     head: "bg-slate-50 border-slate-100 text-slate-800" },
};

// ---------- shared bits ----------
const Pill = ({ children, tone = "indigo" }) => (
  <span className={`px-2 py-0.5 rounded-md text-[10px] font-black tracking-wider uppercase ${TONE_CLASSES[tone].chip}`}>{children}</span>
);

const AiPill = ({ status }) => {
  const s = String(status || "pending");
  const map = {
    green:   { cls: "bg-emerald-100 text-emerald-800",     label: "GREEN · auto-eligible" },
    red:     { cls: "bg-rose-100 text-rose-800",           label: "RED · human gate" },
    pending: { cls: "bg-slate-100 text-slate-700",         label: "PENDING" },
    sent:    { cls: "bg-indigo-100 text-indigo-800",       label: "SENT" },
    blocked: { cls: "bg-slate-200 text-slate-700",         label: "BLOCKED" },
  };
  const m = map[s] || map.pending;
  return <span className={`px-2 py-0.5 rounded-md text-[10px] font-black tracking-wider ${m.cls}`}>{m.label}</span>;
};

const KpiTile = ({ label, value, tone, testId }) => (
  <div data-testid={testId} className="bg-white rounded-2xl border border-slate-200 p-4">
    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-black tracking-widest ${tone}`}>{label}</div>
    <div className="mt-2 text-3xl font-black tabular-nums" style={{ color: NEXON.indigoInk }}>{value}</div>
  </div>
);

const Input = ({ label, val, onChange, placeholder, type = "text", testId }) => (
  <div>
    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</label>
    <input
      data-testid={testId}
      type={type}
      value={val ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
    />
  </div>
);

// ---------- Thread Drawer ----------
function ThreadDrawer({ emailId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    try {
      const r = await adminApi.get(`/admin/support/emails/${emailId}`);
      setData(r.data);
      setDraft(r.data.ai_draft || "");
    } catch { toast.error("Failed to load thread"); }
  };
  useEffect(() => { if (emailId) load(); }, [emailId]);
  if (!emailId) return null;

  const regenerate = async (tone) => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/support/emails/${emailId}/regenerate`, { tone });
      setDraft(r.data.ai_draft);
      toast.success(`Draft regenerated${tone ? ` (${tone})` : ""}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regenerate failed");
    } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/support/emails/${emailId}/approve`, { body: draft });
      toast.success("Sent (mock) — logged in history");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error("Add a reason"); return; }
    setBusy(true);
    try {
      await adminApi.post(`/admin/support/emails/${emailId}/reject`, { reason: rejectReason });
      toast.success("Marked as handled manually");
      onChanged?.();
      onClose();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const pauseSender = async () => {
    if (!data?.from_email) return;
    try {
      await adminApi.post(`/admin/support/sender-pause`, { sender: data.from_email, action: "pause" });
      toast.success(`Auto-replies paused for ${data.from_email}`);
    } catch { toast.error("Failed"); }
  };

  const meta = data?.category ? CATEGORY_META[data.category] : null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="support-thread-drawer">
      <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="w-full max-w-[720px] bg-white h-full overflow-y-auto shadow-2xl border-l border-slate-200">
        {!data ? (
          <div className="p-16 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={16} /> Loading thread…
          </div>
        ) : (
          <div className="p-6 lg:p-8">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <AiPill status={data.ai_status} />
                  {meta && <Pill tone={meta.tone}>{meta.label}</Pill>}
                  {data.reference_cited && (
                    <span className="text-[11px] font-mono font-black text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">{data.reference_cited}</span>
                  )}
                  {data.banned_hit && <Pill tone="rose">BANNED: {data.banned_hit}</Pill>}
                </div>
                <h2 className="mt-2 text-xl font-black" style={{ color: NEXON.indigoInk }}>{data.subject || "(no subject)"}</h2>
                <div className="text-xs text-slate-500 mt-1">from <span className="font-bold text-slate-700">{data.from_email}</span> · {new Date(data.created_at).toLocaleString()}</div>
              </div>
              <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="drawer-close"><X size={20} /></button>
            </div>

            {/* Inbound message */}
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <div className="flex items-center gap-2 mb-2">
                <User2 size={14} className="text-slate-500" />
                <span className="text-[10px] font-black tracking-widest uppercase text-slate-500">Customer said</span>
              </div>
              <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed">{data.body}</pre>
            </div>

            {/* AI draft */}
            <div className="mt-6 rounded-2xl border border-indigo-200 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-50 via-white to-emerald-50 border-b border-indigo-100">
                <div className="flex items-center gap-2 text-indigo-800">
                  <Bot size={14} />
                  <span className="text-[10px] font-black tracking-widest uppercase">Miles · draft reply</span>
                </div>
                {data.draft_meta?.reference_found === false && data.category === "submission_status" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-rose-700 bg-rose-100 px-2 py-0.5 rounded">
                    <AlertTriangle size={11} /> Reference not found · auto-send blocked
                  </span>
                )}
              </div>
              <textarea
                data-testid="draft-textarea"
                rows={10}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="w-full p-5 text-sm text-slate-800 leading-relaxed focus:outline-none font-sans"
              />
              <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50/70">
                <button data-testid="regen-btn" disabled={busy} onClick={() => regenerate()} className="inline-flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-indigo-300 hover:text-indigo-700">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Regenerate
                </button>
                <button disabled={busy} onClick={() => regenerate("warmer")}  className="text-xs font-black px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-emerald-300 hover:text-emerald-700">Warmer</button>
                <button disabled={busy} onClick={() => regenerate("firmer")}  className="text-xs font-black px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-rose-300 hover:text-rose-700">Firmer</button>
                <button disabled={busy} onClick={() => regenerate("shorter")} className="text-xs font-black px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-slate-400">Shorter</button>
                <span className="flex-1" />
                <button onClick={pauseSender} className="inline-flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-lg text-slate-600 hover:text-rose-700"><PauseCircle size={12} /> Pause sender</button>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <button
                data-testid="approve-send-btn"
                onClick={approve}
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-black text-sm shadow-sm hover:opacity-95 disabled:opacity-50"
                style={{ backgroundColor: NEXON.indigoInk }}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Approve &amp; send (mock)
              </button>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">Reject / mark handled</div>
                <div className="flex gap-2">
                  <input
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="reason (e.g. replied via phone)"
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-xs"
                    data-testid="reject-reason"
                  />
                  <button data-testid="reject-btn" onClick={reject} disabled={busy} className="text-xs font-black px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white">Reject</button>
                </div>
              </div>
            </div>

            {/* Thread history */}
            {data.thread_history?.length > 0 && (
              <div className="mt-8">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">Past emails from {data.from_email}</div>
                <div className="space-y-2">
                  {data.thread_history.map((t, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 p-3 bg-white">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-slate-800 truncate">{t.subject}</span>
                        <AiPill status={t.ai_status} />
                        <span className="text-[10px] text-slate-400 ml-auto">{new Date(t.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="text-xs text-slate-500 line-clamp-2 mt-1">{t.body}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

// ---------- Main page ----------
export default function AdminSupportInbox() {
  const [tab, setTab] = useState("inbox");
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState({ total: 0, pending: 0, red: 0, auto_today: 0 });
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [autoRules, setAutoRules] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [syncing, setSyncing] = useState(false);

  // template drafts (local before save)
  const [tplDraft, setTplDraft] = useState({});
  const [bannedDraft, setBannedDraft] = useState([]);
  const [newBanned, setNewBanned] = useState("");
  const [mailbox, setMailbox] = useState({
    email: "", imap_host: "imap.titan.email", imap_port: 993,
    smtp_host: "smtp.titan.email", smtp_port: 465, password: "",
    imap_ssl: true, smtp_ssl: true, auto_poll_minutes: 5,
    telegram_chat_id: "", telegram_bot_token: "",
  });
  const [persona, setPersona] = useState({ support_name: "Miles", title: "Support Specialist", company: "GC Nexon", submission_url: "" });
  const [autoSend, setAutoSend] = useState({ master: false, categories: { submission_status: true, how_to_submit: true, general_question: true, payment_issue: false, dispute_fraud: false } });
  const [alerts, setAlerts] = useState({ enabled: true, sensitivity: "high" });
  const [testingConn, setTestingConn] = useState(false);
  const [autoForm, setAutoForm] = useState({ trigger: "", category: "general_question", reply: "", enabled: true, match_type: "contains" });
  const [testTo, setTestTo] = useState("");

  const loadEmails = async (nextFilter = filter, nextQuery = query) => {
    try {
      const params = {};
      if (nextFilter !== "all") params.status = nextFilter;
      if (nextQuery) params.q = nextQuery;
      const r = await adminApi.get("/admin/support/emails", { params });
      setItems(r.data.items || []);
      setKpis(r.data.kpis || { total: 0, pending: 0, red: 0, auto_today: 0 });
    } catch { /* silent */ }
  };
  const loadConfig = async () => {
    try {
      const r = await adminApi.get("/admin/support/config");
      setConfig(r.data);
      setTplDraft(r.data.templates || {});
      setBannedDraft(r.data.banned_phrases || []);
      setMailbox((m) => ({ ...m, ...(r.data.mailbox || {}), password: "", telegram_bot_token: "" }));
      if (r.data.persona) setPersona((p) => ({ ...p, ...r.data.persona }));
      if (r.data.auto_send) setAutoSend((a) => ({ ...a, ...r.data.auto_send, categories: { ...a.categories, ...(r.data.auto_send.categories || {}) } }));
      if (r.data.alerts) setAlerts((a) => ({ ...a, ...r.data.alerts }));
    } catch { /* silent */ }
  };
  const loadHistory = async () => {
    try {
      const r = await adminApi.get("/admin/support/history");
      setHistory(r.data || []);
    } catch { /* silent */ }
  };
  const loadRules = async () => {
    try {
      const r = await adminApi.get("/admin/support/auto-replies");
      setAutoRules(r.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { loadEmails(); loadConfig(); loadRules(); loadHistory(); }, []);

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await adminApi.post("/admin/support/sync");
      toast.success(`Sync complete · fetched ${r.data.fetched} email${r.data.fetched === 1 ? "" : "s"}`, { description: r.data.mocked ? "Mocked — wire Titan IMAP for live fetch." : "" });
      await Promise.all([loadEmails(), loadConfig()]);
    } catch {
      toast.error("Sync failed");
    } finally { setSyncing(false); }
  };

  const saveTemplates = async () => {
    try {
      await adminApi.post("/admin/support/config", { templates: tplDraft });
      toast.success("Templates saved");
      await loadConfig();
    } catch { toast.error("Save failed"); }
  };
  const saveBanned = async () => {
    try {
      await adminApi.post("/admin/support/config", { banned_phrases: bannedDraft });
      toast.success("Banned phrases saved");
      await loadConfig();
    } catch { toast.error("Save failed"); }
  };
  const saveMailbox = async () => {
    try {
      const payload = { mailbox: { ...mailbox } };
      if (!payload.mailbox.password) delete payload.mailbox.password;
      if (!payload.mailbox.telegram_bot_token) delete payload.mailbox.telegram_bot_token;
      await adminApi.post("/admin/support/config", payload);
      toast.success("Mailbox saved");
      setMailbox((m) => ({ ...m, password: "", telegram_bot_token: "" }));
      await loadConfig();
    } catch { toast.error("Save failed"); }
  };
  const runTest = async () => {
    if (!testTo) { toast.error("Enter a test recipient"); return; }
    try {
      const r = await adminApi.post("/admin/support/test", { to: testTo });
      toast.success("Test email queued (mock)", { description: r.data.note });
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };
  const testConnection = async () => {
    setTestingConn(true);
    try {
      const r = await adminApi.post("/admin/support/test-connection");
      if (r.data.ok) toast.success("Connection OK (mocked)", { description: r.data.message });
      else toast.error("Not fully configured", { description: r.data.message });
    } catch { toast.error("Test failed"); }
    finally { setTestingConn(false); }
  };
  const savePersona = async () => {
    try {
      await adminApi.post("/admin/support/config", { persona });
      toast.success("Persona saved");
      await loadConfig();
    } catch { toast.error("Save failed"); }
  };
  const saveAutoSend = async () => {
    try {
      await adminApi.post("/admin/support/config", { auto_send: autoSend });
      toast.success("Auto-send whitelist saved");
      await loadConfig();
    } catch { toast.error("Save failed"); }
  };
  const saveAlerts = async () => {
    try {
      await adminApi.post("/admin/support/config", { alerts });
      toast.success("Alert settings saved");
      await loadConfig();
    } catch { toast.error("Save failed"); }
  };
  const sendTestAlert = async () => {
    try {
      const r = await adminApi.post("/admin/support/test-alert");
      if (r.data.ok) toast.success("Test alert sent (mocked)");
      else toast.error("Not connected", { description: r.data.message });
    } catch { toast.error("Failed"); }
  };
  const connectTwoWay = async () => {
    try {
      const currently = !!config?.mailbox?.two_way_connected;
      const r = await adminApi.post("/admin/support/connect-two-way", { action: currently ? "disconnect" : "connect" });
      toast.success(r.data.two_way_connected ? "Two-way replies connected (mock)" : "Two-way replies disconnected");
      await loadConfig();
    } catch { toast.error("Failed"); }
  };
  const unlockDispatcher = async () => {
    try {
      await adminApi.post("/admin/support/unlock");
      toast.success("Dispatcher unlocked");
      await loadConfig();
    } catch { toast.error("Failed"); }
  };
  const resumeSender = async (sender) => {
    try {
      await adminApi.post("/admin/support/sender-pause", { sender, action: "resume" });
      toast.success(`Resumed ${sender}`);
      await loadConfig();
    } catch { toast.error("Failed"); }
  };
  const addBanned = () => {
    const v = newBanned.trim();
    if (!v) return;
    if (bannedDraft.includes(v)) { toast.error("Already in list"); return; }
    setBannedDraft([...bannedDraft, v]);
    setNewBanned("");
  };
  const saveRule = async () => {
    if (!autoForm.trigger.trim() || !autoForm.reply.trim()) { toast.error("Trigger + reply required"); return; }
    try {
      await adminApi.post("/admin/support/auto-replies", autoForm);
      toast.success("Auto-reply rule saved");
      setAutoForm({ trigger: "", category: "general_question", reply: "", enabled: true, match_type: "contains" });
      await loadRules();
    } catch { toast.error("Save failed"); }
  };
  const deleteRule = async (rid) => {
    try {
      await adminApi.delete(`/admin/support/auto-replies/${rid}`);
      toast.success("Rule removed");
      await loadRules();
    } catch { toast.error("Failed"); }
  };

  const filteredItems = useMemo(() => items, [items]);
  const syncStatus = config?.sync_status;

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 lg:p-8" style={{ backgroundColor: NEXON.cloud }}>
        {/* Header */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] font-black tracking-widest uppercase text-indigo-600">Support inbox</span>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight mt-1" style={{ color: NEXON.indigoInk }} data-testid="support-inbox-title">
              AI-drafted replies · human gate
            </h1>
            <p className="text-sm mt-1" style={{ color: NEXON.ash }}>
              Titan IMAP polled every 2 min · Miles drafts replies · nothing sends until you approve.
            </p>
            <div className="mt-3 h-[3px] w-24 rounded-full" style={{ backgroundImage: `linear-gradient(90deg, ${NEXON.indigo}, ${NEXON.indigoInk}, ${NEXON.mint})` }} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-white border border-slate-200 font-black text-slate-600">
              <Clock size={12} className="text-indigo-500" />
              {syncStatus?.last_sync_at ? `Last synced ${new Date(syncStatus.last_sync_at).toLocaleTimeString()}` : "Not synced yet"}
            </div>
            <button
              data-testid="sync-now-btn"
              onClick={runSync}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-black shadow-sm disabled:opacity-60"
              style={{ backgroundColor: NEXON.indigo }}
            >
              {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Sync now
            </button>
          </div>
        </div>

        {/* KPI row */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiTile testId="support-kpi-pending" label="PENDING REVIEW"  value={kpis.pending}    tone="bg-amber-100 text-amber-800" />
          <KpiTile testId="support-kpi-red"     label="RED · SENSITIVE" value={kpis.red}        tone="bg-rose-100 text-rose-800" />
          <KpiTile testId="support-kpi-auto"    label="SENT TODAY"      value={kpis.auto_today} tone="bg-emerald-100 text-emerald-800" />
          <KpiTile testId="support-kpi-total"   label="TOTAL IN QUEUE"  value={kpis.total}      tone="bg-indigo-100 text-indigo-800" />
        </div>

        {/* Dispatcher lockout warning */}
        {config?.lockout_until && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-center gap-3">
            <AlertTriangle size={16} className="text-rose-600" />
            <div className="text-sm text-rose-800 flex-1">Dispatcher locked out after 2 strikes. No auto-sends until you unlock.</div>
            <button onClick={unlockDispatcher} className="inline-flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-lg bg-rose-600 text-white hover:bg-rose-700"><Unlock size={12} /> Unlock</button>
          </div>
        )}

        {/* Sub-tabs */}
        <div className="mt-6 flex items-center gap-1 border-b border-slate-200 flex-wrap">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`inbox-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`relative inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-black transition-colors ${active ? "" : "text-slate-500 hover:text-slate-800"}`}
                style={active ? { color: NEXON.indigoInk } : {}}
              >
                <Icon size={14} /> {t.label}
                {active && <span className="absolute left-2 right-2 -bottom-px h-[3px] rounded-t-full" style={{ backgroundImage: `linear-gradient(90deg, ${NEXON.indigo}, ${NEXON.indigoInk}, ${NEXON.mint})` }} />}
              </button>
            );
          })}
        </div>

        {/* Inbox */}
        {tab === "inbox" && (
          <div className="mt-6">
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <div className="flex-1 min-w-[260px] flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200">
                <Search size={14} className="text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadEmails(filter, query)}
                  placeholder="search subject, body, sender, reference…"
                  className="flex-1 text-sm bg-transparent focus:outline-none"
                  data-testid="support-search"
                />
              </div>
              <div className="flex items-center gap-1 px-1 py-1 rounded-xl bg-white border border-slate-200">
                {[
                  { k: "all",     l: "All" },
                  { k: "pending", l: "Pending" },
                  { k: "red",     l: "Red" },
                  { k: "green",   l: "Green" },
                  { k: "sent",    l: "Sent" },
                ].map((o) => (
                  <button
                    key={o.k}
                    data-testid={`filter-${o.k}`}
                    onClick={() => { setFilter(o.k); loadEmails(o.k, query); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-black ${filter === o.k ? "text-white" : "text-slate-500 hover:text-slate-800"}`}
                    style={filter === o.k ? { backgroundColor: NEXON.indigoInk } : {}}
                  >{o.l}</button>
                ))}
              </div>
              <button
                onClick={() => loadEmails(filter, query)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-black bg-white border border-slate-200 hover:border-indigo-300"
              ><Filter size={12} /> Apply</button>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
              {filteredItems.length ? filteredItems.map((it) => {
                const meta = it.category ? CATEGORY_META[it.category] : null;
                return (
                  <button
                    key={it.id}
                    data-testid={`support-row-${it.id}`}
                    onClick={() => setOpenId(it.id)}
                    className="w-full text-left p-5 hover:bg-slate-50/60 transition group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1 h-2 w-2 rounded-full" style={{ backgroundColor: it.ai_status === "red" ? "#ef4444" : it.ai_status === "sent" ? NEXON.indigo : it.ai_status === "green" ? "#10b981" : "#94a3b8" }} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-black text-slate-900 truncate">{it.subject || "(no subject)"}</span>
                          <AiPill status={it.ai_status} />
                          {meta && <Pill tone={meta.tone}>{meta.label}</Pill>}
                          {it.reference_cited && <span className="text-[10px] font-mono font-black text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">{it.reference_cited}</span>}
                          {it.banned_hit && <Pill tone="rose">BANNED HIT</Pill>}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          from <span className="font-bold text-slate-700">{it.from_email}</span> · {new Date(it.created_at).toLocaleString()}
                        </div>
                        <div className="text-sm text-slate-700 mt-2 line-clamp-2">{it.body}</div>
                      </div>
                      <ArrowRight size={16} className="text-slate-300 group-hover:text-indigo-500 mt-1" />
                    </div>
                  </button>
                );
              }) : (
                <div className="p-16 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
                  <Inbox size={28} className="text-slate-300" />
                  Inbox is empty. Hit <span className="font-black text-slate-600">Sync now</span> to pull the latest from Titan.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sent history */}
        {tab === "history" && (
          <div className="mt-6 bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
            {history.length ? history.map((h) => (
              <div key={h.id} className="p-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <CheckCircle2 size={14} className="text-emerald-600" />
                  <span className="font-black text-slate-900">{h.subject}</span>
                  {h.category && CATEGORY_META[h.category] && <Pill tone={CATEGORY_META[h.category].tone}>{CATEGORY_META[h.category].label}</Pill>}
                  <span className="text-[10px] text-slate-400 ml-auto">{new Date(h.sent_at).toLocaleString()}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">to <span className="font-bold text-slate-700">{h.to_email}</span> · by {h.sent_by}</div>
                <pre className="whitespace-pre-wrap text-sm text-slate-700 mt-3 font-sans line-clamp-3">{h.body}</pre>
              </div>
            )) : (
              <div className="p-16 text-center text-slate-400 text-sm">No sent replies yet.</div>
            )}
          </div>
        )}

        {/* Auto-replies */}
        {tab === "auto" && (
          <div className="mt-6 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={14} className="text-indigo-600" />
                <span className="text-[10px] font-black tracking-widest uppercase text-indigo-700">Add auto-reply rule</span>
              </div>
              <p className="text-sm text-slate-500">If an inbound email matches the trigger, Miles auto-uses this reply as the base draft. Nothing sends without human approval unless auto-send is enabled.</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <Input testId="rule-trigger" label="Trigger phrase" val={autoForm.trigger} onChange={(v) => setAutoForm({ ...autoForm, trigger: v })} placeholder="e.g. 'payment delay'" />
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Category</label>
                  <select
                    value={autoForm.category}
                    onChange={(e) => setAutoForm({ ...autoForm, category: e.target.value })}
                    className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:border-indigo-400"
                    data-testid="rule-category"
                  >
                    {Object.entries(CATEGORY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Suggested reply body</label>
                  <textarea
                    rows={4}
                    value={autoForm.reply}
                    onChange={(e) => setAutoForm({ ...autoForm, reply: e.target.value })}
                    placeholder="Hi {name}, thanks for reaching out…"
                    className="mt-1.5 w-full p-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-indigo-400"
                    data-testid="rule-reply"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button data-testid="save-rule" onClick={saveRule} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-black" style={{ backgroundColor: NEXON.indigoInk }}>
                  <Plus size={14} /> Save rule
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
              {autoRules.length ? autoRules.map((r) => (
                <div key={r.id} className="p-5 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-black text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">{r.trigger}</span>
                      {CATEGORY_META[r.category] && <Pill tone={CATEGORY_META[r.category].tone}>{CATEGORY_META[r.category].label}</Pill>}
                      <Pill tone={r.enabled ? "emerald" : "slate"}>{r.enabled ? "ENABLED" : "OFF"}</Pill>
                    </div>
                    <div className="text-sm text-slate-700 mt-2 line-clamp-2">{r.reply}</div>
                  </div>
                  <button onClick={() => deleteRule(r.id)} className="text-slate-400 hover:text-rose-600" data-testid={`rule-delete-${r.id}`}><X size={16} /></button>
                </div>
              )) : (
                <div className="p-12 text-center text-slate-400 text-sm">No rules yet — add your first above.</div>
              )}
            </div>
          </div>
        )}

        {/* Templates + Banned phrases now live inside Settings tab (see below) */}

        {/* Connections */}
        {tab === "connections" && (
          <div className="mt-6 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2">
                <Link2 size={14} className="text-indigo-600" />
                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">Two-way Telegram</span>
              </div>
              <h3 className="mt-1 text-lg font-black" style={{ color: NEXON.indigoInk }}>Ring my phone on RED emails</h3>
              <p className="text-sm text-slate-500 mt-1">When an inbound email is flagged RED (frustration or fraud), we push it to your Telegram. Your Telegram reply posts straight into the customer chat.</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <Input testId="tg-chat-id" label="Telegram chat ID" val={mailbox.telegram_chat_id} onChange={(v) => setMailbox({ ...mailbox, telegram_chat_id: v })} placeholder="e.g. 123456789" />
                <Input testId="tg-bot-token" type="password" label="Telegram bot token" val={mailbox.telegram_bot_token} onChange={(v) => setMailbox({ ...mailbox, telegram_bot_token: v })} placeholder={config?.mailbox?.telegram_bot_token_set ? "•••• already saved" : "paste bot token"} />
              </div>
              <div className="mt-4 flex justify-end">
                <button onClick={saveMailbox} data-testid="save-telegram" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-black" style={{ backgroundColor: NEXON.indigoInk }}><Send size={14} /> Connect Telegram</button>
              </div>
              <div className="mt-3 text-xs text-slate-500">Get your chat ID from @userinfobot on Telegram. Create the bot via @BotFather to get a bot token.</div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-emerald-600" />
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Paused senders</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">Auto-replies are muted for the addresses below. Manually reply from the Inbox instead.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(config?.paused_senders || []).length ? config.paused_senders.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 text-xs font-bold">
                    <PauseCircle size={12} /> {s}
                    <button onClick={() => resumeSender(s)} className="ml-1 text-emerald-600 hover:text-emerald-800" title="resume"><PlayCircle size={12} /></button>
                  </span>
                )) : <div className="text-xs text-slate-400">No senders paused.</div>}
              </div>
            </div>
          </div>
        )}

        {/* Titan mailbox settings */}
        {tab === "settings" && (
          <div className="mt-6 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2">
                <Settings2 size={14} className="text-indigo-600" />
                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">Titan mailbox</span>
              </div>
              <h3 className="mt-1 text-lg font-black" style={{ color: NEXON.indigoInk }}>IMAP + SMTP credentials</h3>
              <p className="text-sm text-slate-500 mt-1">Password is stored encrypted at rest (Fernet). Leave the password blank to keep the current one.</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <Input testId="mb-email"    label="Support email"        val={mailbox.email}     onChange={(v) => setMailbox({ ...mailbox, email: v })} placeholder="support@gcnexon.com" />
                <Input testId="mb-password" label="Encrypted password"   val={mailbox.password}  onChange={(v) => setMailbox({ ...mailbox, password: v })} type="password" placeholder={config?.mailbox?.password_set ? "•••• already saved" : "••••••••"} />
                <Input testId="mb-imap-host" label="IMAP host"           val={mailbox.imap_host} onChange={(v) => setMailbox({ ...mailbox, imap_host: v })} />
                <Input testId="mb-imap-port" label="IMAP port"           val={mailbox.imap_port} onChange={(v) => setMailbox({ ...mailbox, imap_port: Number(v) })} type="number" />
                <Input testId="mb-smtp-host" label="SMTP host"           val={mailbox.smtp_host} onChange={(v) => setMailbox({ ...mailbox, smtp_host: v })} />
                <Input testId="mb-smtp-port" label="SMTP port"           val={mailbox.smtp_port} onChange={(v) => setMailbox({ ...mailbox, smtp_port: Number(v) })} type="number" />
                <Input testId="mb-poll"      label="Auto-poll every (minutes)" val={mailbox.auto_poll_minutes} onChange={(v) => setMailbox({ ...mailbox, auto_poll_minutes: Number(v) })} type="number" />
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">SSL / TLS</label>
                  <div className="mt-1.5 flex items-center gap-4 h-[42px]">
                    <label className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={!!mailbox.imap_ssl} onChange={(e) => setMailbox({ ...mailbox, imap_ssl: e.target.checked })} data-testid="mb-imap-ssl" className="w-4 h-4 accent-indigo-600" />
                      IMAP SSL
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={!!mailbox.smtp_ssl} onChange={(e) => setMailbox({ ...mailbox, smtp_ssl: e.target.checked })} data-testid="mb-smtp-ssl" className="w-4 h-4 accent-indigo-600" />
                      SMTP SSL
                    </label>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap justify-end gap-2">
                <button onClick={testConnection} disabled={testingConn} data-testid="test-connection" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-slate-200 hover:border-indigo-300 text-sm font-black text-slate-700">
                  {testingConn ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Test connection
                </button>
                <button onClick={saveMailbox} data-testid="save-mailbox" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-black" style={{ backgroundColor: NEXON.indigoInk }}>
                  <Save size={14} /> Save mailbox
                </button>
              </div>
            </div>

            {/* Persona & signature */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2">
                <User2 size={14} className="text-indigo-600" />
                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">Persona &amp; signature</span>
              </div>
              <h3 className="mt-1 text-lg font-black" style={{ color: NEXON.indigoInk }}>The signature block used in every reply</h3>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <Input testId="persona-name"   label="Support name"    val={persona.support_name}  onChange={(v) => setPersona({ ...persona, support_name: v })} placeholder="Miles" />
                <Input testId="persona-title"  label="Title"           val={persona.title}         onChange={(v) => setPersona({ ...persona, title: v })} placeholder="Support Specialist" />
                <Input testId="persona-company" label="Company"        val={persona.company}       onChange={(v) => setPersona({ ...persona, company: v })} placeholder="GC Nexon" />
                <Input testId="persona-url"    label="Submission URL"  val={persona.submission_url} onChange={(v) => setPersona({ ...persona, submission_url: v })} placeholder="https://gcnexon.com/submit" />
              </div>
              <div className="mt-6 flex justify-end">
                <button onClick={savePersona} data-testid="save-persona" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-black" style={{ backgroundColor: NEXON.indigoInk }}>
                  <Save size={14} /> Save persona
                </button>
              </div>
            </div>

            {/* Auto-send whitelist */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-emerald-600" />
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Auto-send whitelist</span>
              </div>
              <h3 className="mt-1 text-lg font-black" style={{ color: NEXON.indigoInk }}>Which categories send without your review?</h3>
              <p className="text-sm text-slate-500 mt-1">Payment / fraud drafts are acknowledgment-only (no amounts, dates, or admissions). Any draft that trips a banned phrase is always held.</p>

              <label className={`mt-5 flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer ${autoSend.master ? "border-indigo-300 bg-indigo-50/60" : "border-slate-200"}`}>
                <input type="checkbox" checked={autoSend.master} onChange={(e) => setAutoSend({ ...autoSend, master: e.target.checked })} data-testid="autosend-master" className="w-5 h-5 accent-indigo-600" />
                <div>
                  <div className="font-black text-sm" style={{ color: NEXON.indigoInk }}>Master switch</div>
                  <div className="text-xs text-slate-500">Globally enable auto-send. Off = every email requires your manual approval.</div>
                </div>
              </label>

              <div className="mt-4 grid sm:grid-cols-2 gap-2">
                {Object.entries(CATEGORY_META).filter(([k]) => k !== "spam_irrelevant").map(([k, m]) => (
                  <label key={k} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer ${autoSend.categories?.[k] ? "border-indigo-200 bg-indigo-50/40" : "border-slate-200 bg-white"} ${!autoSend.master ? "opacity-50" : ""}`}>
                    <input type="checkbox" disabled={!autoSend.master} checked={!!autoSend.categories?.[k]} onChange={(e) => setAutoSend({ ...autoSend, categories: { ...autoSend.categories, [k]: e.target.checked } })} data-testid={`autosend-${k}`} className="w-4 h-4 accent-indigo-600" />
                    <span className="text-sm font-bold text-slate-800">{m.label}</span>
                  </label>
                ))}
              </div>

              <div className="mt-6 flex justify-end">
                <button onClick={saveAutoSend} data-testid="save-autosend" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-black" style={{ backgroundColor: NEXON.indigoInk }}>
                  <Save size={14} /> Save
                </button>
              </div>
            </div>

            {/* Frustrated-customer phone alerts */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <MessageCircleWarning size={14} className="text-rose-600" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-rose-700">Frustrated-customer phone alerts</span>
                  </div>
                  <h3 className="mt-1 text-lg font-black" style={{ color: NEXON.indigoInk }}>Get an instant Telegram ping when a customer sounds upset</h3>
                </div>
                <Pill tone={config?.mailbox?.telegram_bot_token_set && config?.mailbox?.telegram_chat_id ? "emerald" : "slate"}>
                  {config?.mailbox?.telegram_bot_token_set && config?.mailbox?.telegram_chat_id ? "✓ Telegram connected" : "Not connected"}
                </Pill>
              </div>

              <label className={`mt-5 flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer ${alerts.enabled ? "border-rose-200 bg-rose-50/60" : "border-slate-200"}`}>
                <input type="checkbox" checked={alerts.enabled} onChange={(e) => setAlerts({ ...alerts, enabled: e.target.checked })} data-testid="alerts-enabled" className="w-5 h-5 accent-rose-600" />
                <div>
                  <div className="font-black text-sm" style={{ color: NEXON.indigoInk }}>Enable frustrated-customer alerts</div>
                  <div className="text-xs text-slate-500">Ping the moment an upset customer emails in.</div>
                </div>
              </label>

              <div className="mt-4">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Alert sensitivity</label>
                <select
                  value={alerts.sensitivity}
                  onChange={(e) => setAlerts({ ...alerts, sensitivity: e.target.value })}
                  data-testid="alerts-sensitivity"
                  className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:border-indigo-400"
                >
                  <option value="high">High signal — only clearly angry or dispute/fraud emails (fewer pings)</option>
                  <option value="medium">Medium — angry + delayed payments + escalations</option>
                  <option value="low">Low — every negative sentiment (loud phone)</option>
                </select>
              </div>

              <div className="mt-6 flex flex-wrap justify-end gap-2">
                <button onClick={sendTestAlert} data-testid="send-test-alert" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-slate-200 hover:border-rose-300 text-sm font-black text-slate-700">
                  <TestTube2 size={14} /> Send test alert
                </button>
                <button onClick={saveAlerts} data-testid="save-alerts" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-black" style={{ backgroundColor: NEXON.indigoInk }}>
                  <Save size={14} /> Save
                </button>
              </div>
            </div>

            {/* Two-way Telegram */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Link2 size={14} className="text-indigo-600" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">Answer chats from Telegram (two-way)</span>
                  </div>
                  <h3 className="mt-1 text-lg font-black" style={{ color: NEXON.indigoInk }}>Reply to customers straight from Telegram</h3>
                  <p className="text-sm text-slate-500 mt-1">When a customer asks for a human or sounds angry, you get the chat in Telegram. Reply once to claim the customer, then keep typing — every message posts straight into their chat on the website.</p>
                </div>
                <Pill tone={config?.mailbox?.two_way_connected ? "emerald" : "slate"}>
                  {config?.mailbox?.two_way_connected ? "✓ Connected" : "Not connected"}
                </Pill>
              </div>
              <div className="mt-5">
                <button onClick={connectTwoWay} data-testid="connect-two-way" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-black" style={{ backgroundColor: NEXON.indigoInk }}>
                  <ShieldCheck size={14} /> {config?.mailbox?.two_way_connected ? "Disconnect two-way replies" : "Connect two-way replies"}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2">
                <TestTube2 size={14} className="text-emerald-600" />
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Send test email</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">Currently mocked. Real SMTP send activates when the Titan credentials above are saved and validated.</p>
              <div className="mt-4 flex gap-2">
                <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-indigo-400" data-testid="test-to" />
                <button onClick={runTest} data-testid="run-test" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-black" style={{ backgroundColor: NEXON.indigoInk }}>
                  <TerminalSquare size={14} /> Fire test
                </button>
              </div>
            </div>

            {syncStatus && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2">
                  <RefreshCw size={14} className="text-indigo-600" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">Sync status</span>
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Last sync</div>
                    <div className="text-slate-800 font-black">{syncStatus.last_sync_at ? new Date(syncStatus.last_sync_at).toLocaleString() : "never"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Next check</div>
                    <div className="text-slate-800 font-black">{syncStatus.next_check_at ? new Date(syncStatus.next_check_at).toLocaleString() : "on demand"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Status</div>
                    <div className={`font-black ${syncStatus.last_sync_ok === false ? "text-rose-700" : "text-emerald-700"}`}>{syncStatus.last_sync_ok === false ? "ERRORED" : "OK"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Auto-poll</div>
                    <div className="text-slate-800 font-black">{syncStatus.enabled ? "every 2 min" : "off"}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Banned phrases */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2">
                <Ban size={14} className="text-rose-600" />
                <span className="text-[10px] font-black uppercase tracking-widest text-rose-700">Banned phrases</span>
              </div>
              <h3 className="mt-1 text-lg font-black" style={{ color: NEXON.indigoInk }}>Any draft containing these is forced to RED and cannot auto-send</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {bannedDraft.map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-rose-200 bg-rose-50 text-rose-800 text-xs font-bold">
                    {p}
                    <button onClick={() => setBannedDraft(bannedDraft.filter((_, x) => x !== i))} className="text-rose-500 hover:text-rose-700"><X size={12} /></button>
                  </span>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <input value={newBanned} onChange={(e) => setNewBanned(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addBanned()} placeholder="add phrase..." data-testid="banned-input" className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-indigo-400" />
                <button onClick={addBanned} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-white border border-slate-200 hover:border-indigo-300 text-sm font-black text-slate-700"><Plus size={14} /> Add</button>
                <button onClick={saveBanned} data-testid="save-banned" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-black" style={{ backgroundColor: NEXON.indigoInk }}><Save size={14} /> Save</button>
              </div>
            </div>

            {/* Reply templates */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2">
                <MailCheck size={14} className="text-indigo-600" />
                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">Reply templates</span>
              </div>
              <h3 className="mt-1 text-lg font-black" style={{ color: NEXON.indigoInk }}>Soft guidance for the AI per category</h3>
              <p className="text-sm text-slate-500 mt-1">Keep them brief and tone-setting.</p>
              <div className="mt-5 space-y-4">
                {Object.entries(CATEGORY_META).map(([key, m]) => (
                  <div key={key}>
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">{m.label}</label>
                    <textarea
                      value={tplDraft[key] || ""}
                      onChange={(e) => setTplDraft({ ...tplDraft, [key]: e.target.value })}
                      rows={3}
                      className="mt-1.5 w-full text-sm text-slate-700 leading-relaxed rounded-lg border border-slate-200 p-3 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                      data-testid={`tpl-${key}`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button data-testid="save-templates" onClick={saveTemplates} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-black" style={{ backgroundColor: NEXON.indigoInk }}>
                  <Save size={14} /> Save templates
                </button>
              </div>
            </div>

            <p className="text-xs text-slate-400 px-1">
              <ShieldCheck className="inline w-3 h-3 mr-1" /> Password is stored encrypted (Fernet) — never displayed, never logged.
            </p>
          </div>
        )}
      </div>

      {openId && <ThreadDrawer emailId={openId} onClose={() => setOpenId(null)} onChanged={() => { loadEmails(); loadHistory(); loadConfig(); }} />}
    </AdminLayout>
  );
}
